import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import {
  AppConfig,
  APP_CONFIG,
  TraceService,
  HTTPTraceHeader,
  SpanTypes,
} from '@sidkik/global';
import { Observable, catchError, tap, throwError } from 'rxjs';

export interface UserAPI {
  enableGoogleCalendarSync(
    googleId: string,
    trace?: HTTPTraceHeader
  ): Observable<void>;
  disableGoogleCalendarSync(
    googleId: string,
    trace?: HTTPTraceHeader
  ): Observable<void>;
}

@Injectable({
  providedIn: 'root',
})
export class UserService implements UserAPI {
  constructor(
    @Inject(APP_CONFIG) readonly tenantConfig: AppConfig,
    private readonly http: HttpClient,
    private readonly traceService: TraceService
  ) {}

  private processHeaders(trace?: HTTPTraceHeader): HttpHeaders {
    let headers = new HttpHeaders({
      'ngsw-bypass': 'bypass',
      'Cache-Control': 'no-cache',
    });

    if (trace) {
      headers = new HttpHeaders({
        Traceparent: trace?.traceparent ?? '',
        Tracestate: trace?.tracestate ?? '',
        'ngsw-bypass': 'bypass',
        'Cache-Control': 'no-cache',
        'Access-Control-Expose-Headers':
          'Traceparent,TraceState,Orig-Tracestate,Orig-Traceparent,X-Tp', // expose the trace headers in response
      });
    }
    return headers;
  }

  enableGoogleCalendarSync(
    googleId: string,
    trace?: HTTPTraceHeader
  ): Observable<void> {
    let internalTrace = false;
    if (!trace) {
      this.traceService.startSpan(SpanTypes.adminEnableGoogleCalendarSync);
      trace = this.traceService.getHTTPHeaderPropagators(
        SpanTypes.adminEnableGoogleCalendarSync
      );
      internalTrace = true;
    }
    return this.http
      .put<void>(
        `${this.tenantConfig.api.endpoint}/admin/google/calendar/sync/enable`,
        { id: googleId },
        {
          headers: this.processHeaders(trace),
        }
      )
      .pipe(
        catchError((err: any) => {
          internalTrace &&
            this.traceService.endSpan(
              SpanTypes.adminEnableGoogleCalendarSync,
              err
            );
          return throwError(() => err);
        }),
        tap(() => {
          internalTrace &&
            this.traceService.endSpan(SpanTypes.adminEnableGoogleCalendarSync);
        })
      );
  }

  disableGoogleCalendarSync(
    googleId: string,
    trace?: HTTPTraceHeader
  ): Observable<void> {
    let internalTrace = false;
    if (!trace) {
      this.traceService.startSpan(SpanTypes.adminDisableGoogleCalendarSync);
      trace = this.traceService.getHTTPHeaderPropagators(
        SpanTypes.adminDisableGoogleCalendarSync
      );
      internalTrace = true;
    }
    return this.http
      .put<void>(
        `${this.tenantConfig.api.endpoint}/admin/google/calendar/sync/disable`,
        { id: googleId },
        {
          headers: this.processHeaders(trace),
        }
      )
      .pipe(
        catchError((err: any) => {
          internalTrace &&
            this.traceService.endSpan(
              SpanTypes.adminDisableGoogleCalendarSync,
              err
            );
          return throwError(() => err);
        }),
        tap(() => {
          internalTrace &&
            this.traceService.endSpan(SpanTypes.adminDisableGoogleCalendarSync);
        })
      );
  }

  updateName(trace?: HTTPTraceHeader): Observable<void> {
    let internalTrace = false;
    if (!trace) {
      this.traceService.startSpan(SpanTypes.profileUpdateName);
      trace = this.traceService.getHTTPHeaderPropagators(
        SpanTypes.profileUpdateName
      );
      internalTrace = true;
    }
    return this.http
      .put<void>(`${this.tenantConfig.api.endpoint}/admin/authz/name`, {
        headers: this.processHeaders(trace),
      })
      .pipe(
        catchError((err: any) => {
          internalTrace &&
            this.traceService.endSpan(SpanTypes.profileUpdateName, err);
          return throwError(() => err);
        }),
        tap(() => {
          internalTrace &&
            this.traceService.endSpan(SpanTypes.profileUpdateName);
        })
      );
  }

  updateAvatar(trace?: HTTPTraceHeader): Observable<void> {
    let internalTrace = false;
    if (!trace) {
      this.traceService.startSpan(SpanTypes.profileUpdateAvatar);
      trace = this.traceService.getHTTPHeaderPropagators(
        SpanTypes.profileUpdateAvatar
      );
      internalTrace = true;
    }
    return this.http
      .put<void>(`${this.tenantConfig.api.endpoint}/admin/authz/avatar`, {
        headers: this.processHeaders(trace),
      })
      .pipe(
        catchError((err: any) => {
          internalTrace &&
            this.traceService.endSpan(SpanTypes.profileUpdateAvatar, err);
          return throwError(() => err);
        }),
        tap(() => {
          internalTrace &&
            this.traceService.endSpan(SpanTypes.profileUpdateAvatar);
        })
      );
  }
}
